import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { writeStoredListCreatedBy } from '../lib/createdByScopeStorage';
import { isOverviewAdjuster, isOverviewPrivileged } from '../lib/portalRoles';
import { FALLBACK_STATUS_LABELS } from '../lib/referralStatus';
import { ensureIncidentLookup, } from '../services/incidentLookup';
import { fetchOverviewUsers, } from '../services/referrals';
import { ensureSignInUserInfo } from '../services/signinUserInfo';
import { DEFAULT_REFERRAL_FILTERS } from '../types/referral';
function statusFilterOptions(statuses) {
    const fromLookup = statuses.map((entry) => ({
        value: entry.value,
        label: entry.name,
    }));
    const options = fromLookup.length > 0
        ? fromLookup
        : Object.entries(FALLBACK_STATUS_LABELS).map(([value, label]) => ({
            value: Number(value),
            label,
        }));
    return [{ value: null, label: 'All statuses' }, ...options];
}
function userDisplayName(user) {
    const name = `${user.firstName} ${user.lastName}`.trim();
    return name || user.email || `User ${user.id}`;
}
function sortUsersByDisplayName(users) {
    return [...users].sort((a, b) => {
        const cmp = userDisplayName(a).localeCompare(userDisplayName(b), undefined, {
            sensitivity: 'base',
        });
        if (cmp !== 0)
            return cmp;
        return a.id - b.id;
    });
}
function roleNameFromDetail(detail) {
    return (detail?.roleName?.trim() ||
        (typeof detail?.role?.name === 'string' ? detail.role.name.trim() : '') ||
        '');
}
export function ReferralFiltersBar({ filters, onChange }) {
    const [expanded, setExpanded] = useState(true);
    const [draft, setDraft] = useState(filters);
    const [statuses, setStatuses] = useState([]);
    const [viewerId, setViewerId] = useState(null);
    const [viewerRoleName, setViewerRoleName] = useState('');
    const [roleReady, setRoleReady] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const privileged = isOverviewPrivileged(viewerRoleName);
    const adjuster = isOverviewAdjuster(viewerRoleName);
    const showCreatedBy = privileged || adjuster;
    const sortedUsers = useMemo(() => sortUsersByDisplayName(users), [users]);
    useEffect(() => {
        setDraft(filters);
    }, [filters]);
    useEffect(() => {
        ensureIncidentLookup()
            .then((lookup) => setStatuses(lookup.statuses))
            .catch((err) => console.error('[incidentLookup] statuses failed', err));
    }, []);
    useEffect(() => {
        let cancelled = false;
        ensureSignInUserInfo()
            .then((info) => {
            if (cancelled)
                return;
            const detail = info.detail;
            const id = typeof detail?.id === 'number' ? detail.id : null;
            setViewerId(id);
            setViewerRoleName(roleNameFromDetail(detail));
            setRoleReady(true);
        })
            .catch((err) => {
            console.error('[referral-filters] signin user failed', err);
            if (!cancelled)
                setRoleReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        if (!roleReady || !privileged)
            return;
        let cancelled = false;
        setUsersLoading(true);
        fetchOverviewUsers()
            .then((list) => {
            if (!cancelled)
                setUsers(list);
        })
            .catch((err) => {
            console.error('[referral-filters] overview users failed', err);
            if (!cancelled)
                setUsers([]);
        })
            .finally(() => {
            if (!cancelled)
                setUsersLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [roleReady, privileged]);
    const handleSubmit = (event) => {
        event.preventDefault();
        onChange(draft);
    };
    const persistCreatedBy = (createdBy) => {
        if (viewerId == null || (!privileged && !adjuster))
            return;
        writeStoredListCreatedBy(viewerId, adjuster ? 'adjuster' : 'privileged', createdBy);
    };
    const handleReset = () => {
        const reset = {
            ...DEFAULT_REFERRAL_FILTERS,
            // Adjusters default back to "My referrals".
            createdBy: adjuster && viewerId != null ? viewerId : null,
        };
        persistCreatedBy(reset.createdBy);
        setDraft(reset);
        onChange(reset);
    };
    const activeFilterCount = [
        filters.search.trim() !== '',
        filters.status !== null,
        filters.dateFrom !== '',
        filters.dateTo !== '',
        showCreatedBy &&
            (adjuster
                ? viewerId != null && filters.createdBy !== viewerId
                : filters.createdBy != null),
    ].filter(Boolean).length;
    const statusOptions = statusFilterOptions(statuses);
    const createdBySelectValue = (() => {
        if (adjuster) {
            return draft.createdBy != null && viewerId != null && draft.createdBy === viewerId
                ? 'mine'
                : 'org';
        }
        return draft.createdBy != null ? String(draft.createdBy) : 'org';
    })();
    const onCreatedByChange = (value) => {
        if (adjuster) {
            const createdBy = value === 'mine' && viewerId != null ? viewerId : null;
            persistCreatedBy(createdBy);
            setDraft({ ...draft, createdBy });
            return;
        }
        if (value === '' || value === 'org') {
            persistCreatedBy(null);
            setDraft({ ...draft, createdBy: null });
            return;
        }
        const id = Number(value);
        if (!Number.isFinite(id) || id <= 0)
            return;
        persistCreatedBy(id);
        setDraft({ ...draft, createdBy: id });
    };
    return (_jsxs("section", { className: `filters-panel${expanded ? ' is-expanded' : ''}`, children: [_jsxs("button", { type: "button", className: "filters-toggle", "aria-expanded": expanded, "aria-controls": "referral-filters", onClick: () => setExpanded((current) => !current), children: [_jsxs("span", { className: "filters-toggle-label", children: [_jsx("svg", { className: "filters-icon", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M4 5h16l-6.2 7.1v5.4l-3.6 1.8v-7.2L4 5Z" }) }), "Filter"] }), _jsxs("span", { className: "filters-toggle-meta", children: [activeFilterCount > 0 && (_jsxs("span", { className: "filters-active-count", children: [activeFilterCount, " active"] })), _jsx("svg", { className: "filters-chevron", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "m7 9 5 5 5-5" }) })] })] }), expanded && (_jsxs("form", { id: "referral-filters", className: "filters-bar", onSubmit: handleSubmit, children: [_jsxs("label", { className: "filter-field filter-field-grow", children: ["Search referrals", _jsx("input", { type: "search", placeholder: "Claim number or referral ID", value: draft.search, onChange: (e) => setDraft({ ...draft, search: e.target.value }) })] }), _jsxs("label", { className: "filter-field", children: ["Referral status", _jsx("select", { value: draft.status ?? '', onChange: (e) => {
                                    const raw = e.target.value;
                                    setDraft({
                                        ...draft,
                                        status: raw === '' ? null : Number(raw),
                                    });
                                }, children: statusOptions.map((option) => (_jsx("option", { value: option.value ?? '', children: option.label }, `${option.value ?? 'all'}-${option.label}`))) })] }), showCreatedBy && (_jsxs("label", { className: "filter-field", children: ["Created by", _jsx("select", { value: createdBySelectValue, onChange: (e) => onCreatedByChange(e.target.value), disabled: privileged && usersLoading, children: adjuster ? (_jsxs(_Fragment, { children: [_jsx("option", { value: "mine", children: "My referrals" }), _jsx("option", { value: "org", children: "Entire organization" })] })) : (_jsxs(_Fragment, { children: [_jsx("option", { value: "org", children: "Entire organization" }), sortedUsers.map((user) => (_jsxs("option", { value: user.id, children: [userDisplayName(user), user.roleName ? ` (${user.roleName})` : ''] }, user.id)))] })) })] })), _jsxs("label", { className: "filter-field", children: ["Created after", _jsx("input", { type: "date", value: draft.dateFrom, onChange: (e) => setDraft({ ...draft, dateFrom: e.target.value }) })] }), _jsxs("label", { className: "filter-field", children: ["Created before", _jsx("input", { type: "date", value: draft.dateTo, onChange: (e) => setDraft({ ...draft, dateTo: e.target.value }) })] }), _jsxs("div", { className: "filter-actions", children: [_jsx("button", { type: "button", className: "btn btn-secondary", onClick: handleReset, children: "Reset" }), _jsx("button", { type: "submit", className: "btn btn-primary", children: "Search" })] })] }))] }));
}
